/**
 * Сетка школьного столбика: типы и кирпичи, из которых её собирают
 * `./column.ts` (сложение, вычитание, умножение) и `./column-div.ts` (деление
 * уголком). Отдельным файлом, чтобы деление не импортировало модуль, который
 * импортирует его самого.
 *
 * Ключи клеток строго по разделу 2.3 docs/room-math-input-design.md: цифра
 * `r3c5`, запятая при клетке `r3c5,`, клетка «в уме» `k1c4`. Это ключи ответа в
 * `Y.Map`, то есть формат данных в документе урока: менять их нельзя без
 * миграции ответов.
 */

export type ColumnOp = "add" | "sub" | "mul" | "div";

/**
 * Роль строки. `number` это числа условия (у деления делимое и делитель, две
 * строки на одной высоте), `partial` частичное произведение, `result` итог
 * сложения, вычитания и умножения, `quotient` частное, `subtrahend` вычитаемое
 * в уголке, `remainder` разность со снесённой цифрой и последний остаток,
 * `carry` ряд «в уме» над первым числом.
 */
export type ColumnRole = "carry" | "number" | "partial" | "result" | "quotient" | "subtrahend" | "remainder";

/**
 * Запятая при клетке. `given` нарисована условием; `must` ученик ставит сам;
 * `may` можно и не ставить, но только пока правее в строке пусто: у `111,0`
 * ноль после запятой зачёркивают, и тогда запятая не нужна, а без нуля и без
 * запятой `1110` это другое число. `none` запятой здесь быть не должно.
 */
export type ColumnComma = "none" | "given" | "must" | "may";

/**
 * Клетка сетки. `expect` это цифра или пустая строка: клетка должна остаться
 * пустой. `optional` значит «пустая тоже верно»: крайняя левая клетка итога
 * (ширина итога не выдаёт ответ) и конечный ноль после запятой, причём
 * конечные нули пустеют только справа налево, без дыр. `given` это число
 * условия: нарисовано и не правится.
 */
export type ColumnCell = {
  id: string;
  r: number;
  c: number;
  expect: string;
  given: boolean;
  optional: boolean;
  comma: ColumnComma;
};

/**
 * Логическая строка. У деления на одной высоте `r` их бывает две: вычитаемое
 * слева от уголка и частное справа. `sign` это знак действия слева от
 * строки, `ruleBelow` черта под ней.
 *
 * Курсор по строке (5.2): экран ставит его в столбец `start` и после каждой
 * цифры ведёт по `dir` до края строки; если по другую сторону от `start`
 * остались клетки, курсор перескакивает на соседнюю с `start` клетку и идёт
 * в обратную сторону. У обычной строки `start` это край, откуда пишут
 * (правый у `rtl`, левый у `ltr`, первая клетка, которую правят, у делимого),
 * и второго хода нет. Второй ход нужен разности в уголке со снесённой
 * цифрой: разность считают от единиц, поэтому `start` это её правая цифра
 * (столбец под последней цифрой вычитаемого), курсор идёт влево до конца
 * разности, а потом вправо по снесённым цифрам. Если разность ноль и не
 * пишется (`824 : 4`, строка «24»), `start` это левая клетка, и строка
 * пишется слева направо целиком.
 */
export type ColumnLine = {
  role: ColumnRole;
  r: number;
  dir: "rtl" | "ltr";
  start: number;
  sign: string | null;
  ruleBelow: boolean;
  cells: ColumnCell[];
  /** Клетки запятой версии 2 слева направо; у версии 1 всегда пусто. */
  commas: ColumnCommaCell[];
  /**
   * Как судится запятая строки. `cell` по клеткам, как у версии 1; `row` у
   * итога умножения и у частного с запятой: место запятой там ответ, и красить
   * клетки значит его выдавать (4.3 docs/column-decimal-cell-design.md).
   */
  commaVerdict: "cell" | "row";
};

/**
 * Клетка запятой версии 2: узкая колонка на границе между столбцами `b` и
 * `b + 1`. Ключ ответа тот же, что у запятой версии 1 при клетке `b` (`r5c2,`),
 * поэтому ответы у двух версий одни. Вид это вид клетки-владельца слева.
 */
export type ColumnCommaCell = {
  id: string;
  r: number;
  b: number;
  kind: ColumnComma;
};

/**
 * Геометрия сетки, общая у версий 1 и 2: её строят сложение, умножение и
 * деление. Столбцы и строки считаются с единицы слева и сверху, ряд «в уме»
 * стоит на высоте 0. `split` есть только у деления: после этого столбца
 * проходит вертикальная черта уголка. `header` это строка перевода
 * десятичного делителя (`2,5 : 0,5 = 25 : 5`) или перестановки при вычитании
 * большего из меньшего (`561,2 - 577,6 = -(577,6 - 561,2)`, минус U+2212).
 */
export type ColumnGrid = {
  op: ColumnOp;
  width: number;
  height: number;
  split: number | null;
  header: string | null;
  lines: ColumnLine[];
};

/** Номер раскладки, который умеет строить этот код. */
export type ColumnLayoutVersion = (typeof COLUMN_LAYOUTS)[number];

/**
 * Раскладка наружу: геометрия плюс номер и границы, у которых есть колонка
 * запятой (`commaCols`, по возрастанию; у версии 1 пусто). Экран, курсор и
 * проверка ветвятся по `version`, а не по наличию колонок: у целой сетки
 * версии 2 колонок нет, но ведёт она себя как версия 2.
 */
export type ColumnLayout = ColumnGrid & { version: ColumnLayoutVersion; commaCols: number[] };

export type ColumnError =
  | "empty"
  | "not_understood"
  | "too_many_digits"
  | "division_by_zero"
  | "bad_places"
  | "unknown_layout"
  | "too_big";

/** Цифр в каждом числе условия, раздел 2.3. */
export const COLUMN_MAX_DIGITS = 8;
/** Знаков после запятой в частном, раздел 2.3. */
export const COLUMN_MAX_PLACES = 4;
/** Ключей в ответе столбика, потолок схемы (2.3): без него лишний ответ молча выпадает из снимка. */
export const COLUMN_MAX_KEYS = 400;
/** Столбцов в сетке: шире на слайде рядом с картинкой задания не поместится. */
export const COLUMN_MAX_WIDTH = 24;
/**
 * Номера раскладок, которые строит этот код. Номер хранится в элементе рядом с
 * условием (поле `layout` схемы): версия 1 ставит запятую меткой на угол
 * клетки, версия 2 в свою узкую клетку на границе (docs/column-decimal-cell-design.md).
 * Геометрия и ключи ответа у них общие, различаются ввод, показ и суждение о
 * запятой.
 *
 * ⚠️ Номер принимается по этому списку, а не сравнением с `COLUMN_LAYOUT`:
 * иначе в день переключения перестала бы строиться архивная версия 1. Список
 * повторён литералами в схеме элемента, и их совпадение держит тест.
 */
export const COLUMN_LAYOUTS = [1, 2] as const;
/**
 * Номер, который пишут НОВЫЕ элементы: 2 с выкатки 2 (раздел 9
 * docs/column-decimal-cell-design.md). Элементы версии 1 в уроках, архиве и
 * заготовках остаются версией 1 и строятся по `COLUMN_LAYOUTS`; столбик из
 * заготовки переводится в 2 только при засеве урока (`compileLessonExercises`
 * в apps/api, развилка Р1). ⚠️ Назад на 1 не переключать: элементы версии 2
 * уже лежат в документах и заготовках, и откат номера их не вернёт.
 */
export const COLUMN_LAYOUT = 2;
/**
 * Ширина колонки запятой версии 2 в клетках. Одно число на коробку элемента и
 * шаблон сетки у веба: в двух местах оно разошлось бы, и сетка вылезла бы за
 * коробку.
 */
export const COLUMN_COMMA_RATIO = 0.35;

export function cellKey(r: number, c: number): string {
  return `r${r}c${c}`;
}

export function commaKey(id: string): string {
  return `${id},`;
}

/**
 * Ключ вердикта строки версии 2 (`row:5`). В ответе его нет никогда, только в
 * вердикте `checkColumn`: это не клетка, и красить по нему нечего.
 */
export function rowKey(r: number): string {
  return `row:${r}`;
}

export function carryKey(c: number): string {
  return `k1c${c}`;
}

/** Десятичное число: целая часть без ведущих нулей (кроме одного), дробная как набрана. */
export type Dec = { int: string; frac: string };

export function parseDec(s: string): Dec | null {
  const m = /^(\d+)(?:[.,](\d+))?$/.exec(s);
  if (m === null) return null;
  return { int: (m[1] as string).replace(/^0+(?=\d)/, ""), frac: m[2] ?? "" };
}

export function fmtDec(d: Dec): string {
  return d.frac === "" ? d.int : `${d.int},${d.frac}`;
}

export function cell(r: number, c: number, expect: string, given: boolean): ColumnCell {
  return { id: cellKey(r, c), r, c, expect, given, optional: false, comma: "none" };
}

/** Цифры, прижатые правым краем к столбцу `end`. */
export function place(r: number, digits: string, end: number, given: boolean): ColumnCell[] {
  const start = end - digits.length + 1;
  return [...digits].map((d, k) => cell(r, start + k, d, given));
}

/**
 * Число условия, прижатое так, что его последняя цифра целой части стоит в
 * столбце `intEnd`. Дробная часть дополняется нулями до `scale` знаков: при
 * сложении и вычитании короткое число пишут с нулями (`10,00 - 0,01`), и эти
 * нули нарисованы, как и запятая.
 */
export function givenNumber(r: number, d: Dec, intEnd: number, scale = d.frac.length): ColumnCell[] {
  const frac = d.frac.padEnd(scale, "0");
  const cells = place(r, d.int + frac, intEnd + frac.length, true);
  const last = cells.find((x) => x.c === intEnd);
  if (last !== undefined && frac !== "") last.comma = "given";
  return cells;
}

/**
 * Клетки итога или частного. `digits` уже содержит не меньше `scale + 1` цифр
 * (ведущий ноль у `0,06` это клетка), прижаты к `end`, а клетки левее них до
 * `from` должны остаться пустыми. Конечные нули после запятой необязательны, и
 * если нулей в дробной части только они, необязательна и запятая (5.4).
 */
export function answerCells(r: number, digits: string, scale: number, from: number, end: number): ColumnCell[] {
  const start = end - digits.length + 1;
  const cells: ColumnCell[] = [];
  for (let c = from; c < start; c++) cells.push({ ...cell(r, c, "", false), optional: true });
  cells.push(...place(r, digits, end, false));
  if (scale === 0) return cells;
  const frac = cells.slice(-scale);
  for (let k = frac.length - 1; k >= 0 && frac[k]?.expect === "0"; k--) (frac[k] as ColumnCell).optional = true;
  const intLast = cells[cells.length - scale - 1] as ColumnCell;
  intLast.comma = frac.every((x) => x.optional) ? "may" : "must";
  return cells;
}

/** Число с `scale` знаками после запятой: цифры с ведущими нулями до `scale + 1`. */
export function withScale(v: bigint, scale: number): string {
  return v.toString().padStart(scale + 1, "0");
}

export function line(role: ColumnRole, r: number, cells: ColumnCell[], more: Partial<ColumnLine> = {}): ColumnLine {
  const dir = more.dir ?? "rtl";
  const editable = cells.filter((x) => !x.given);
  const cols = (editable.length > 0 ? editable : cells).map((x) => x.c);
  const start = dir === "rtl" ? Math.max(...cols) : Math.min(...cols);
  return { role, r, dir, start, sign: null, ruleBelow: false, cells, commas: [], commaVerdict: "cell", ...more };
}

/**
 * Сколько ключей может набраться в ответе: цифра в каждой клетке, которую
 * правят, запятая при любой клетке, кроме нарисованной, ряд «в уме» и метка
 * вида. Считается худший случай, а не ожидаемый: ученик ставит запятые и
 * туда, где их нет.
 */
export function keyBudget(lines: readonly ColumnLine[]): number {
  let n = 1;
  for (const l of lines) {
    for (const x of l.cells) n += (x.given ? 0 : 1) + (l.role === "carry" || x.comma === "given" ? 0 : 1);
  }
  return n;
}
